/**
 * Order Notification Hook - Enhanced with Volume Control
 * Plays sound and shows notification when new orders arrive
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { toast } from 'sonner';

// Get/Set volume from localStorage
const getStoredVolume = () => {
  const stored = localStorage.getItem('liantiao_notification_volume');
  return stored ? parseFloat(stored) : 0.7;
};

const setStoredVolume = (volume) => {
  localStorage.setItem('liantiao_notification_volume', volume.toString());
};

// Create notification sound using Web Audio API
const createNotificationSound = (volume = 0.7) => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  
  const playSound = (vol = volume) => {
    const now = audioContext.currentTime;
    const masterGain = audioContext.createGain();
    masterGain.gain.setValueAtTime(vol, now);
    masterGain.connect(audioContext.destination);
    
    // Create a more attention-grabbing cash register/cha-ching sound
    // First: Bell-like tone
    const osc1 = audioContext.createOscillator();
    const gain1 = audioContext.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(1046.5, now); // C6
    gain1.gain.setValueAtTime(0.4 * vol, now);
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
    osc1.connect(gain1);
    gain1.connect(masterGain);
    osc1.start(now);
    osc1.stop(now + 0.2);
    
    // Second: Higher bell
    const osc2 = audioContext.createOscillator();
    const gain2 = audioContext.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1318.51, now + 0.08); // E6
    gain2.gain.setValueAtTime(0.35 * vol, now + 0.08);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.28);
    osc2.connect(gain2);
    gain2.connect(masterGain);
    osc2.start(now + 0.08);
    osc2.stop(now + 0.28);
    
    // Third: Highest bell (resolution)
    const osc3 = audioContext.createOscillator();
    const gain3 = audioContext.createGain();
    osc3.type = 'sine';
    osc3.frequency.setValueAtTime(1567.98, now + 0.16); // G6
    gain3.gain.setValueAtTime(0.5 * vol, now + 0.16);
    gain3.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
    osc3.connect(gain3);
    gain3.connect(masterGain);
    osc3.start(now + 0.16);
    osc3.stop(now + 0.5);
    
    // Fourth: Add a subtle coin/register sound effect
    const osc4 = audioContext.createOscillator();
    const gain4 = audioContext.createGain();
    osc4.type = 'triangle';
    osc4.frequency.setValueAtTime(2093, now + 0.3); // C7
    gain4.gain.setValueAtTime(0.2 * vol, now + 0.3);
    gain4.gain.exponentialRampToValueAtTime(0.01, now + 0.45);
    osc4.connect(gain4);
    gain4.connect(masterGain);
    osc4.start(now + 0.3);
    osc4.stop(now + 0.45);
  };
  
  return playSound;
};

// Singleton audio player
let notificationPlayer = null;
let audioInitialized = false;
let currentVolume = getStoredVolume();

const initializeAudio = () => {
  if (!audioInitialized && typeof window !== 'undefined') {
    try {
      notificationPlayer = createNotificationSound(currentVolume);
      audioInitialized = true;
    } catch (e) {
      console.warn('Audio initialization failed:', e);
    }
  }
};

export const playOrderSound = (volume = currentVolume) => {
  initializeAudio();
  if (notificationPlayer) {
    try {
      notificationPlayer(volume);
    } catch (e) {
      console.warn('Sound playback failed:', e);
    }
  }
};

export const setNotificationVolume = (volume) => {
  currentVolume = volume;
  setStoredVolume(volume);
};

export const getNotificationVolume = () => currentVolume;

export const useOrderNotification = (options = {}) => {
  const {
    enabled = true,
    soundEnabled = true,
    toastEnabled = true,
    onNewOrder = null
  } = options;
  
  const [volume, setVolume] = useState(getStoredVolume());
  const previousCountRef = useRef(null);
  const isFirstRender = useRef(true);
  
  // Update volume
  const updateVolume = useCallback((newVolume) => {
    setVolume(newVolume);
    setNotificationVolume(newVolume);
  }, []);
  
  // Initialize audio on user interaction
  const initAudio = useCallback(() => {
    initializeAudio();
  }, []);
  
  const notifyNewOrder = useCallback((orderCount, orderDetails = null) => {
    if (!enabled) return;
    
    // Play sound with current volume
    if (soundEnabled) {
      playOrderSound(volume);
    }
    
    // Show toast notification
    if (toastEnabled) {
      const message = "🎉 You've got an order!";
      const description = orderCount > 1 
        ? `${orderCount} new orders received!`
        : orderDetails?.customer 
          ? `New order from ${orderDetails.customer}`
          : 'New order received!';
      
      toast.success(message, {
        description,
        duration: 8000,
        action: {
          label: 'View Orders',
          onClick: () => window.location.href = '/admin/fulfillment'
        }
      });
    }
    
    // Custom callback
    if (onNewOrder) {
      onNewOrder(orderCount, orderDetails);
    }
  }, [enabled, soundEnabled, toastEnabled, volume, onNewOrder]);
  
  const checkForNewOrders = useCallback((currentCount) => {
    // Skip first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousCountRef.current = currentCount;
      return;
    }
    
    // Check if count increased
    if (previousCountRef.current !== null && currentCount > previousCountRef.current) {
      const newOrders = currentCount - previousCountRef.current;
      notifyNewOrder(newOrders);
    }
    
    previousCountRef.current = currentCount;
  }, [notifyNewOrder]);
  
  // Initialize audio on component mount (requires user interaction first)
  useEffect(() => {
    const handleInteraction = () => {
      initAudio();
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
    
    document.addEventListener('click', handleInteraction);
    document.addEventListener('keydown', handleInteraction);
    
    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, [initAudio]);
  
  return {
    checkForNewOrders,
    notifyNewOrder,
    playSound: (vol) => playOrderSound(vol || volume),
    initAudio,
    volume,
    setVolume: updateVolume
  };
};

export default useOrderNotification;
